import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Customer,
  CustomerDealDeduplicationEvent,
  PhoneValidationResult,
} from "@/core/entities/customer";
import { Deal } from "@/core/entities/deal";
import {
  SUBMISSION_REVIEW_DATETIME_FORMAT,
  formatPhoneType,
  formatResponseTime,
  formatValidationStatus,
  getLatestDeduplicationEvent,
  getLatestPhoneValidation,
  getLatestResubmission,
  getValidationBadgeClassName,
  parseReviewDate,
  reviewTimestampToMillis,
} from "@/utils/submission-review";
import { format, formatDistanceToNow } from "date-fns";
import {
  Activity,
  BadgeInfo,
  ChevronDown,
  Clock3,
  CopyCheck,
  Phone,
  Repeat,
  ShieldAlert,
} from "lucide-react";

const summaryCardClassName = "rounded-lg border bg-card p-4";

function RelativeTime({
  value,
}: Readonly<{ value?: Parameters<typeof parseReviewDate>[0] | null }>) {
  const date = parseReviewDate(value);
  if (!date) {
    return <span>-</span>;
  }

  return (
    <span title={format(date, SUBMISSION_REVIEW_DATETIME_FORMAT)}>
      {formatDistanceToNow(date, { addSuffix: true })}
    </span>
  );
}

export function PhoneValidationCard({
  result,
  history,
  title = "Phone Validation",
  onHistoryOpenChange,
}: Readonly<{
  result?: PhoneValidationResult | null;
  history?: PhoneValidationResult[];
  title?: string;
  onHistoryOpenChange?: (open: boolean) => void;
}>) {
  const latestResult = getLatestPhoneValidation(history, result);
  const validationHistory = history ?? (latestResult ? [latestResult] : []);

  return (
    <div className={summaryCardClassName}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Phone className="h-4 w-4" />
            {title}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Latest validation status and quick carrier details.
          </p>
        </div>
        <Badge className={getValidationBadgeClassName(latestResult)}>
          {formatValidationStatus(latestResult)}
        </Badge>
      </div>

      {latestResult ? (
        <div className="mt-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Submitted Phone
              </p>
              <p className="text-sm font-medium">
                {latestResult.originalPhone}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Normalized
              </p>
              <p className="text-sm">
                {latestResult.normalizedPhone ?? latestResult.originalPhone}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Type
              </p>
              <p className="text-sm">
                {formatPhoneType(latestResult.phoneType)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Carrier
              </p>
              <p className="text-sm">{latestResult.carrierName ?? "-"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Country
              </p>
              <p className="text-sm">{latestResult.country ?? "-"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Response Time
              </p>
              <p className="text-sm">
                {formatResponseTime(latestResult.responseTimeMs)}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Checked</span>
            <RelativeTime value={latestResult.validationTimestamp} />
          </div>

          {latestResult.errorCode || latestResult.errorMessage ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <div className="flex items-center gap-2 font-medium">
                <ShieldAlert className="h-4 w-4" />
                Validation Note
              </div>
              <p className="mt-1 text-xs leading-5">
                {latestResult.errorCode ?? "No code"}{" "}
                {latestResult.errorMessage ?? ""}
              </p>
            </div>
          ) : null}

          {validationHistory.length > 1 ? (
            <Collapsible onOpenChange={onHistoryOpenChange}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto w-full justify-between px-0 py-1 text-sm font-medium"
                >
                  View History
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="max-h-96 overflow-y-auto pr-3">
                  <div className="space-y-2 pt-2">
                    {validationHistory
                      .slice()
                      .sort(
                        (left, right) =>
                          reviewTimestampToMillis(right.validationTimestamp) -
                          reviewTimestampToMillis(left.validationTimestamp),
                      )
                      .map((item) => (
                        <div
                          key={item.id}
                          className="rounded-md border border-border/70 px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <Badge
                              className={getValidationBadgeClassName(item)}
                            >
                              {formatValidationStatus(item)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              <RelativeTime value={item.validationTimestamp} />
                            </span>
                          </div>
                          <div className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                            <span>{item.originalPhone}</span>
                            <span>
                              {formatPhoneType(item.phoneType)} •{" "}
                              {item.country ?? "-"}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          No phone validation has been recorded yet.
        </p>
      )}
    </div>
  );
}

export function CustomerDeduplicationCard({
  events,
  onHistoryOpenChange,
}: Readonly<{
  events?: CustomerDealDeduplicationEvent[];
  onHistoryOpenChange?: (open: boolean) => void;
}>) {
  const deduplicationEvents = events ?? [];
  const latestEvent = getLatestDeduplicationEvent(deduplicationEvents);

  return (
    <div className={summaryCardClassName}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <CopyCheck className="h-4 w-4" />
            Duplicate Submission Review
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Customer-level duplicate matches recorded during deal submission.
          </p>
        </div>
        <Badge variant="secondary">{deduplicationEvents.length} events</Badge>
      </div>

      {latestEvent ? (
        <div className="mt-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Matched Deal
              </p>
              <p className="text-sm font-medium">
                {latestEvent.matchedExistingDealId}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Resubmission Count
              </p>
              <p className="text-sm">{latestEvent.resubmissionCount}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Product
              </p>
              <p className="text-sm">{latestEvent.submittedProduct}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Quantity
              </p>
              <p className="text-sm">{latestEvent.quantity.toLocaleString()}</p>
            </div>
          </div>

          <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
            <p className="font-medium">{latestEvent.reason}</p>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <Clock3 className="h-3.5 w-3.5" />
              <RelativeTime value={latestEvent.duplicateSubmissionTimestamp} />
            </div>
          </div>

          {deduplicationEvents.length > 1 ? (
            <Collapsible onOpenChange={onHistoryOpenChange}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto w-full justify-between px-0 py-1 text-sm font-medium"
                >
                  View History
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="max-h-96 overflow-y-auto pr-3">
                  <div className="space-y-2 pt-2">
                    {deduplicationEvents
                      .slice()
                      .sort(
                        (left, right) =>
                          reviewTimestampToMillis(
                            right.duplicateSubmissionTimestamp,
                          ) -
                          reviewTimestampToMillis(
                            left.duplicateSubmissionTimestamp,
                          ),
                      )
                      .map((event) => (
                        <div
                          key={event.submissionKey}
                          className="rounded-md border border-border/70 px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-medium">
                              {event.submittedProduct}
                            </p>
                            <span className="text-xs text-muted-foreground">
                              <RelativeTime
                                value={event.duplicateSubmissionTimestamp}
                              />
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {event.quantity.toLocaleString()} • {event.reason}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          No duplicate submissions have been logged for this customer.
        </p>
      )}
    </div>
  );
}

export function DealResubmissionCard({
  deal,
  onHistoryOpenChange,
}: Readonly<{
  deal: Deal;
  onHistoryOpenChange?: (open: boolean) => void;
}>) {
  const resubmissionHistory = deal.resubmissionHistory ?? [];
  const latestResubmission = getLatestResubmission(resubmissionHistory);
  const resubmissionCount = deal.resubmissionCount ?? 0;

  return (
    <div className={summaryCardClassName}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Repeat className="h-4 w-4" />
            Resubmission Activity
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Tracks duplicate submissions that were merged into this deal.
          </p>
        </div>
        <Badge variant={resubmissionCount > 0 ? "default" : "secondary"}>
          {resubmissionCount} repeats
        </Badge>
      </div>

      {resubmissionCount > 0 ? (
        <div className="mt-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Last Submission
              </p>
              <p className="text-sm">
                <RelativeTime
                  value={
                    deal.lastSubmissionAt ?? latestResubmission?.submittedAt
                  }
                />
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                History Entries
              </p>
              <p className="text-sm">{resubmissionHistory.length}</p>
            </div>
          </div>

          {latestResubmission ? (
            <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
              <p className="font-medium">{latestResubmission.reason}</p>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <Activity className="h-3.5 w-3.5" />
                <RelativeTime value={latestResubmission.submittedAt} />
              </div>
            </div>
          ) : null}

          {resubmissionHistory.length > 0 ? (
            <Collapsible onOpenChange={onHistoryOpenChange}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto w-full justify-between px-0 py-1 text-sm font-medium"
                >
                  View History
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="max-h-96 overflow-y-auto pr-3">
                  <div className="space-y-2 pt-2">
                    {resubmissionHistory
                      .slice()
                      .sort(
                        (left, right) =>
                          reviewTimestampToMillis(right.submittedAt) -
                          reviewTimestampToMillis(left.submittedAt),
                      )
                      .map((item) => (
                        <div
                          key={item.submissionKey}
                          className="rounded-md border border-border/70 px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <BadgeInfo className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              <RelativeTime value={item.submittedAt} />
                            </span>
                          </div>
                          <p className="mt-2 text-sm">{item.reason}</p>
                        </div>
                      ))}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          This deal has not been updated by a duplicate submission yet.
        </p>
      )}
    </div>
  );
}

export function CustomerReviewSummary({
  customer,
}: Readonly<{
  customer: Customer;
}>) {
  const latestValidation = getLatestPhoneValidation(
    customer.phoneValidationHistory,
    customer.latestPhoneValidation,
  );
  const latestDeduplicationEvent = getLatestDeduplicationEvent(
    customer.dealDeduplicationEvents,
  );

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div className="rounded-lg border bg-muted/30 p-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Phone Checks
        </p>
        <p className="mt-1 text-xl font-semibold">
          {customer.phoneValidationHistory?.length ??
            (latestValidation ? 1 : 0)}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {formatValidationStatus(latestValidation)}
        </p>
      </div>
      <div className="rounded-lg border bg-muted/30 p-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Duplicate Matches
        </p>
        <p className="mt-1 text-xl font-semibold">
          {customer.dealDeduplicationEvents?.length ?? 0}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {latestDeduplicationEvent ? (
            <RelativeTime
              value={latestDeduplicationEvent.duplicateSubmissionTimestamp}
            />
          ) : (
            "None recorded"
          )}
        </p>
      </div>
      <div className="rounded-lg border bg-muted/30 p-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Latest Response Time
        </p>
        <p className="mt-1 text-xl font-semibold">
          {latestValidation
            ? formatResponseTime(latestValidation.responseTimeMs)
            : "-"}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {latestValidation?.carrierName ?? "No carrier data"}
        </p>
      </div>
    </div>
  );
}
